import React, { useEffect, useState } from 'react';
import { Modal } from '@/components';
import {
  BoxV2 as Box,
  Button,
  DropdownV2 as Dropdown,
  Flex,
} from 'portal-commons';
import { SelectItem } from '@/utils/types';
import { campaignController } from '@/apis/core';
import { toastFlashMessage } from '@/utils';
import { useController, useForm } from 'react-hook-form';

interface Props {
  open: boolean;
  campaignUid: string;
  cnpOptions: SelectItem[];
  onClose: (reloadFlag?: boolean) => void;
}

const ElectCnpRcsModal: React.FC<Props> = ({
  open,
  campaignUid,
  cnpOptions,
  onClose,
}) => {
  const [disabled, setDisabled] = useState(false);
  const { control, clearErrors, reset, handleSubmit } = useForm({
    defaultValues: {
      cnpUid: '',
    },
  });

  const {
    field: { value: cnpUid, onChange: setCnpUid },
    fieldState: { error },
  } = useController({
    control,
    name: 'cnpUid',
    rules: { required: 'Please elect Connectivity Partner' },
  });

  const electCnp = async () => {
    setDisabled(true);
    const response = await campaignController.electCampaignWithCnp(
      campaignUid,
      cnpUid
    );
    if (response) {
      onClose(true);
      toastFlashMessage('Connectivity Partner successfully elected', 'success');
    }
    setDisabled(false);
  };

  useEffect(() => {
    if (!open) {
      clearErrors();
      reset();
    }
  }, [open]);

  return (
    <Modal open={open} testId="electCnpRcsModal">
      <Box
        as="h2"
        sx={{
          my: 12,
          fontSize: 'H600',
          fontWeight: 600,
          lineHeight: '28px',
          color: 't.black80',
        }}
      >
        Elect Connectivity Partner
      </Box>
      <Box
        sx={{
          pt: 7,
          py: 11,
          fontSize: 'H300',
          fontWeight: 400,
          lineHeight: 1.2,
          color: 't.black80',
        }}
      >
        Please identify the upstream partner who will receive the messaging
        campaign.{' '}
        <strong>
          By making a selection, you are automatically accepting the campaign
          that has been shared with you.
        </strong>{' '}
        You will only be able to select CNPs that support RCS.
      </Box>
      <Box sx={{ width: '100%' }}>
        <Dropdown
          label="Elect Connectivity Partner"
          required
          disabled={disabled}
          options={cnpOptions}
          placeholder="Elect Connectivity Partner"
          value={cnpUid}
          onChange={setCnpUid}
          error={!!error?.message}
          helperText={error?.message}
        />
      </Box>
      <Flex
        sx={{
          columnGap: '18px',
          padding: '30px 0',
        }}
      >
        <Button
          variant="outline"
          onClick={() => onClose(false)}
          disabled={disabled}
          data-testid="electCnpEbmModalCancelButton"
        >
          Cancel
        </Button>
        <Button
          disabled={disabled}
          onClick={handleSubmit(electCnp)}
          data-testid="electCnpEbmModalSubmitButton"
        >
          Elect
        </Button>
      </Flex>
    </Modal>
  );
};

export default ElectCnpRcsModal;
